import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Copy, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { KINDS } from "@/components/user-wizard/WizardSteps";

function Line({ label, value, testId }) {
  return (
    <div className="flex gap-3 border-b border-zinc-200 py-1.5 text-sm last:border-0">
      <span className="label-mono w-28 shrink-0 pt-0.5">{label}</span>
      <span className="font-bold" data-testid={testId}>{value}</span>
    </div>
  );
}

export function ReviewStep({ form, opts, summary }) {
  const kind = KINDS.find((k) => k.key === form.kind);
  const guide = opts.guides[form.kind];
  return (
    <div data-testid="wizard-review-step">
      <div className="border border-black bg-white p-3">
        <Line label="Account" value={kind?.label} testId="wizard-review-kind" />
        {summary && <Line label={form.kind === "employee" ? "Role" : form.kind === "media_buyer" ? "Pages" : "Company"} value={summary} testId="wizard-review-company" />}
        <Line label="Name" value={form.name} testId="wizard-review-name" />
        <Line label="Username" value={form.email} testId="wizard-review-email" />
      </div>
      <div className="mt-4 border-l-4 border-black bg-zinc-50 p-3 text-xs" data-testid="wizard-email-preview">
        <div className="label-mono mb-1">Their welcome email will include</div>
        <ul className="list-disc space-y-1 pl-4">
          <li>The sign-in page, their username and a temporary password (made when you press Create). They must choose their own password the first time they sign in.</li>
          {guide.steps.map((s) => <li key={s}>{s}</li>)}
          <li>A button to their guide in the Help Center.</li>
        </ul>
      </div>
    </div>
  );
}

export function CopyPassword({ pw }) {
  const copy = () => navigator.clipboard?.writeText(pw).then(() => toast.success("Copied"));
  return (
    <div className="mt-3 flex items-center gap-2 border-2 border-black bg-white p-2 font-mono text-sm" data-testid="wizard-done-password">
      <span className="flex-1">{pw}</span>
      <button type="button" onClick={copy} className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white" data-testid="wizard-done-copy">
        <Copy size={11} className="inline" /> Copy
      </button>
    </div>
  );
}

export function DoneStep({ result, setResult }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/_admin/user-wizard/accounts/${result.user_id}/resend`);
      setResult({ ...result, email_sent: data.email_sent, temp_password: data.temp_password });
      toast[data.email_sent ? "success" : "error"](data.email_sent ? "Welcome email sent" : "Still couldn't send the email");
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't resend");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="wizard-done-step">
      {result.email_sent ? (
        <div className="flex gap-3 border-2 border-black bg-emerald-50 p-4" data-testid="wizard-done-sent">
          <CheckCircle size={28} weight="fill" className="shrink-0 text-emerald-700" />
          <div className="text-sm">
            <div className="font-bold">{result.name}&apos;s {result.role_label} account is ready.</div>
            <div className="mt-1 text-zinc-700">We emailed <b>{result.email}</b> their username, a temporary password and how to get started.{result.company?.new && ` We also created ${result.company.name}.`}</div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-black bg-amber-50 p-4" data-testid="wizard-done-not-sent">
          <div className="flex gap-2 text-sm font-bold"><WarningCircle size={20} weight="fill" className="text-amber-700" /> The account was created, but the email couldn&apos;t be sent.</div>
          <p className="mt-1 text-xs text-zinc-700">Share these sign-in details another way, or try sending again. Username: <b>{result.email}</b></p>
          <CopyPassword pw={result.temp_password} />
          <button type="button" disabled={busy} onClick={resend} data-testid="wizard-done-resend"
                  className="mt-3 border border-black bg-black px-3 py-1.5 text-xs uppercase tracking-widest text-white transition-colors hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Sending…" : "Try sending again"}
          </button>
        </div>
      )}
    </div>
  );
}
